// @ts-strict-ignore
import { FieldErrorsImpl, UseFormRegister } from 'react-hook-form'
import { Error, ErrorResponse } from './types'

export type UUID = string

export const BankTransferType = {
  SWIFT: 'SWIFT',
} as const

export type BankTransferType = (typeof BankTransferType)[keyof typeof BankTransferType]

export const BeneficiaryTransferDestination = {
  LOCAL: 'LOCAL',
  INTERNATIONAL: 'INTERNATIONAL',
} as const

export type BeneficiaryTransferDestination =
  (typeof BeneficiaryTransferDestination)[keyof typeof BeneficiaryTransferDestination]

export const BeneficiaryEntityType = {
  INDIVIDUAL: 'INDIVIDUAL',
  COMPANY: 'COMPANY',
  // to be deprecated in favour of COMPANY in unifiedpayouts v2 and contacts
  BUSINESS: 'BUSINESS',
} as const

export type BeneficiaryEntityType =
  (typeof BeneficiaryEntityType)[keyof typeof BeneficiaryEntityType]

export const BeneficiaryType = {
  SELF_OWNED: 'SELF_OWNED',
  THIRD_PARTY: 'THIRD_PARTY',
  // to be deprecated in favour of SELF_OWNED in unifiedpayouts v2
  FIRST_PARTY: 'FIRST_PARTY',
} as const

export type BeneficiaryType = (typeof BeneficiaryType)[keyof typeof BeneficiaryType]

export const BeneficiaryDetailsCurrency = {
  default: '',
  EUR: 'EUR',
  GBP: 'GBP',
  NGN: 'NGN',
  USD: 'USD',
  ZAR: 'ZAR',
  AED: 'AED',
  MXN: 'MXN',
  ARS: 'ARS',
  BRL: 'BRL',
} as const

export type BeneficiaryDetailsCurrency =
  (typeof BeneficiaryDetailsCurrency)[keyof typeof BeneficiaryDetailsCurrency]

export const PaymentMethods = {
  // USD
  CUBIX: 'CUBIX',
  ACH: 'ACH',
  ACH_SAME_DAY: 'ACH_SAME_DAY',
  FEDWIRE: 'FEDWIRE',
  FEDNOW: 'FEDNOW',
  CRNOW: 'CRNOW',
  RTP: 'RTP',
  // EUR
  SEPA: 'SEPA_CT',
  SEPA_INSTANT: 'SEPA_INST',
  // GBP
  CHAPS: 'CHAPS',
  BACS: 'BACS',
  FASTER_PAYMENT: 'FASTER_PAYMENT',
  CCC_PAYMENTS: 'CCC_PAYMENTS',
  // International
  SWIFT: 'SWIFT',
} as const

export const PaymentMethodLabels: Record<PaymentMethod, string> = {
  // USD
  CUBIX: 'CUBIX',
  ACH: 'ACH',
  ACH_SAME_DAY: 'Same Day ACH',
  FEDWIRE: 'Fedwire',
  FEDNOW: 'FedNow',
  CRNOW: 'CRNow',
  RTP: 'RTP',
  // EUR
  SWIFT: 'SWIFT',
  SEPA_CT: 'SEPA Credit Transfer',
  SEPA_INST: 'SEPA Instant Credit Transfer',
  // GBP
  CHAPS: 'CHAPS',
  BACS: 'BACS',
  FASTER_PAYMENT: 'Faster Payment',
  CCC_PAYMENTS: 'CCC Payments',
  // International
} as const

export const USDBeneficiaryOptions = {
  CUBIX: 'CUBIX',
  ACH: 'ACH',
  ACH_SAME_DAY: 'ACH_SAME_DAY',
  FEDWIRE: 'FEDWIRE',
  FEDNOW: 'FEDNOW',
  CRNOW: 'CRNOW',
  RTP: 'RTP',
} as const

export const USDPaymentMethodsRequiringAddress = {
  ACH: 'ACH',
  ACH_SAME_DAY: 'ACH_SAME_DAY',
  FEDWIRE: 'FEDWIRE',
  FEDNOW: 'FEDNOW',
  CRNOW: 'CRNOW',
  RTP: 'RTP',
} as const

export type USDPaymentMethodRequiringAddress =
  (typeof USDPaymentMethodsRequiringAddress)[keyof typeof USDPaymentMethodsRequiringAddress]

export const USDBeneficiaryOptionsLabelMapping = {
  CUBIX: 'Cubix',
  ACH: 'ACH',
  ACH_SAME_DAY: 'ACH_SAME_DAY',
  FEDWIRE: 'Fedwire',
  FEDNOW: 'FedNow',
  CRNOW: 'CRNow',
  RTP: 'RTP',
} as const

export type PaymentMethod = (typeof PaymentMethods)[keyof typeof PaymentMethods]

export type Address = {
  addressLine1: string
  addressLine2?: string
  city: string
  postCode: string
  region: string
  country: string
}

export type IntermediaryBank = {
  code: string
  bankName: string
  accountNumber?: string
  bankAccountType?: string
  nid?: string
  bankAddress: {
    addressLine1: string
    addressLine2?: string
    city: string
    postCode: string
    region: string
    country: string
  }
}

export type BeneficiaryDetailsFiatDetails = {
  accountNumber: string
  code: string
  bankName?: string
  bankAddress?: Address
  intermediaryBank?: IntermediaryBank
  accountType?: string
  nid?: string
}

export type BeneficiaryDetails = {
  /**
   * @deprecated Use entityType instead
   */
  type?: BeneficiaryEntityType
  alias?: string
  currency: BeneficiaryDetailsCurrency
  firstName?: string
  beneficiaryType?: BeneficiaryType
  lastName?: string
  businessName?: string
  addressCountry: string
  address?: Address
  transferDestination?: BeneficiaryTransferDestination
  updatedAt?: string
  bvnkAccountReference?: string
  category?: string
  createdAt?: string
  dateOfBirth?: string
  entityType?: BeneficiaryEntityType
  fiat: BeneficiaryDetailsFiatDetails
  reference?: string
  beneficiaryPaymentReference?: string
  transferType?: string
  bankTypeIdentifier?: string
  paymentMethod?: string
  paymentInstrumentId?: string
}

export type AddBeneficiaryRequest = {
  data: BeneficiaryDetails
  authorization: {
    totp: string
  }
}

export type AddBeneficiaryRequestPreRequest = BeneficiaryDetails

export type ChallengeRequest = {
  challenge: UUID
  tokenResponse: string
  mfaCallbackUrl: string
}

export type ModifyFields<T, R> = Omit<T, keyof R> & R

export type AddBeneficiaryChallengeResponse = {
  challenge?: {
    value?: UUID
    type?: string
    ttl?: string
  }
  callbackUrl?: string
  context?: {
    code?: string
    message?: string
  }
  details: {
    documentLink: string
    errors: {
      requestBody: string[]
      createBeneficiaryRequest: string[]
    }
  }
}

export type AddBeneficiaryPostResponse = ErrorResponse & {
  data?: AddBeneficiaryChallengeResponse
}

export interface BanksResponse {
  banks: Bank[]
}

export interface Bank {
  countryCode: string
  bankName: string
  bankIdentifier: string
}

export interface NgBankAccountValidationRequest {
  accountNumber: string
  bankCode: string
}

export interface NgBankAccountValidationResponse {
  data: {
    accountName?: string
    accountNumber?: string
    accountNumberFormat?: string
    bankIdentifier?: string
    errorList: Error[]
  }
  status: number
  statusText: string
  headers: { [property: string]: unknown }
  config: { [property: string]: unknown }
  request: { [property: string]: unknown }
}

export interface BaseErrorResponse {
  code?: string
  data?: {
    challenge?: {
      value?: string
      type?: string
      ttl?: string
    }
    callbackUrl?: string
    code?: string
    message?: string
    errorList?: Error[]
    details?: {
      documentLink?: string
      message?: string
      traceId?: string
      errors?: {
        requestBody?: string[]
        [field: string]: string[]
      }
    }
    traceId?: string
  }
  errorList?: Error[]
  message?: string
  status?: number
}

export type AddBeneficiaryFormErrors = Partial<FieldErrorsImpl<AddBeneficiaryFormPayload>>

export type AddBeneficiaryFormPayloadKeys = keyof AddBeneficiaryFormPayload

export interface BaseFormProps {
  handleChange: (field: string, value: string) => void
  register: UseFormRegister<AddBeneficiaryFormPayload>
}

export interface AddBeneficiaryBasicPayload extends BankAddress {
  beneficiaryPaymentMethod?: string
  alias?: string
  currencyCode?: BeneficiaryDetailsCurrency
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  businessName?: string
  countryCode?: string
  beneficiaryEntityType?: BeneficiaryEntityType
  beneficiaryType?: BeneficiaryType
  beneficiaryPaymentReference?: string
  accountType?: string
  // Intl beneficiaries
  intlIban?: string
  bicSwiftCode?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  postCode?: string
  region?: string
  routingNumber?: string
  // EUR
  iban?: string
  bic?: string
  // GBP
  accountNumber?: string
  sortCode?: string
  // NGN
  ngnAccountNumber?: string
  bankCode?: string
  bankName?: string
  // USD
  usdAccountNumber?: string
  abaRoutingNumber?: string
  cubixAccountNumberDigits?: string
  cbitBankCode?: string
  usdAccountNumberConfirm?: string
  // ZAR
  zarAccountNumber?: string
  zarAccountType?: string
  zarRoutingNumberBranchCode?: string
  zarBank?: string
}

export type AddBeneficiaryBasicPayloadKeys = keyof AddBeneficiaryBasicPayload

export interface BankAddress {
  bankName?: string
  bankAddressLine1?: string
  bankAddressLine2?: string
  bankCity?: string
  bankPostCode?: string
  bankRegion?: string
  bankCountry?: string
  bankCountryName?: string
}

export interface AddBeneficiaryBankDetailsPayload
  extends BankAddress, AddIntermediaryBankDetailsPayload {
  intlIban?: string
  bicSwiftCode?: string
  routingNumber?: string
}

export type AddBeneficiaryBankDetailsPayloadKeys = keyof AddBeneficiaryBankDetailsPayload

export interface AddIntermediaryBankDetailsPayload {
  intermediaryBankName?: string
  intermediaryBic?: string
  intermediaryBankRoutingNumber?: string // USD
  intermediaryBankAddressLine1?: string
  intermediaryBankAddressLine2?: string
  intermediaryBankCity?: string
  intermediaryBankPostCode?: string
  intermediaryBankRegion?: string
  intermediaryBankCountry?: string
  intermediaryBankCountryName?: string
}

export type AddIntermediaryBankDetailsPayloadKeys = keyof AddIntermediaryBankDetailsPayload

export interface AddBeneficiaryFormPayload
  extends
    AddBeneficiaryBasicPayload,
    AddBeneficiaryBankDetailsPayload,
    AddIntermediaryBankDetailsPayload {
  transferDestination?: BeneficiaryTransferDestination
  countryName?: string
  bankCountryName?: string
  password?: string
  challengeCode?: UUID
  mfaCallbackUrl?: string
  skipIntermediaryBankScreen?: boolean
}

export type AddBeneficiaryFormPayloadErrors = Record<
  keyof AddBeneficiaryFormPayload,
  { message: string }
>

export interface UpdateBeneficiaryFormPayload {
  alias: string
  password: string
  businessName?: string
  firstName?: string
  lastName?: string
}

export type UpdateBeneficiaryFormPayloadKeys = keyof UpdateBeneficiaryFormPayload

export type UpdateBeneficiaryFormErrors = Partial<FieldErrorsImpl<UpdateBeneficiaryFormPayload>>

export interface AddLocalBeneficiaryFormPayload {
  currencyCode?: BeneficiaryDetailsCurrency
  firstName?: string
  lastName?: string
  businessName?: string
  countryCode?: string
  // EUR
  iban?: string
  bic?: string
  // GBP
  accountNumber?: string
  sortCode?: string
  // NGN
  ngnAccountNumber?: string
  bankCode?: string
  // USD
  usdAccountNumber?: string
  abaRoutingNumber?: string
  cubixAccountNumberDigits?: string
  cbitBankCode?: string
}

export interface AddLocalBeneficiaryFormConfirmPayload {
  password: string
  isDetailsCorrect: boolean
}

export interface ValidateIbanRequest {
  accountNumberFormat: string
  accountNumber: string
}

export interface AccountValidationBankAddress {
  addressLine1?: string
  addressLine2?: string
  city?: string
  region?: string
  postCode?: string
  countryCode?: string
  fullAddress?: string
}

export interface ValidateIbanResponse {
  bankAccount?: {
    type?: string
    bankName?: string
    accountNumber?: string
    bankCode?: string
    bankAddress?: AccountValidationBankAddress
  }
  validationFailed: string[]
  supportedPaymentMethods: string[]
}

export interface ValidateRoutingNumberResponse {
  bankDetails?: ValidateRoutingNumberBankDetails
  validationFailed: string[]
}

export interface ValidateRoutingNumberBankDetails {
  bankName?: string
  bankCode?: string
  bankAddress?: AccountValidationBankAddress
}

export interface ValidateBicSwiftCodeResponse extends ValidateRoutingNumberResponse {}

export interface ValidateScanRequest {
  sortCode: string
  accountNumber: string
}

export interface ValidateScanResponse {
  bankAccount?: {
    type?: string
    bankName?: string
    accountNumber?: string
    bankCode?: string
    bankAddress?: {
      addressLine1?: string
      addressLine2?: string
      city?: string
      region?: string
      postCode?: string
      countryCode?: string
      fullAddress?: string
    }
  }
  bic: string
  iban: string
  validationFailed: string[]
  supportedPaymentMethods: string[]
}

export interface ValidatBicRequest {
  bic?: string
}

export const EditBeneficiaryModalStep = {
  EDIT_BENEFICIARY: 'EDIT_BENEFICIARY',
  CONFIRMATION: 'CONFIRMATION',
} as const

export type EditBeneficiaryModalStep =
  (typeof EditBeneficiaryModalStep)[keyof typeof EditBeneficiaryModalStep]
